// @ts-nocheck
// Anonymous per-recipe events for the planner popularity stats (POST /api/events on vhp-planner).
// Payload is { e, id, slot, src } only: no customer, session or client id (queue lives in memory). Sent only
// with analytics consent from Shopify's cookie banner. Funnel steps count once per browser and Berlin day: after
// consent, the step keys already sent today are kept in localStorage (no id, only today's step|value keys).
(() => {
  'use strict';

  if (window.vhpPlannerEvents) return;

  const DEFAULT_API = 'https://vhp-planner.vercel.app';
  const EVENTS = new Set([
    'recipe_shown',
    'recipe_liked',
    'recipe_skipped',
    'recipe_planned',
    'recipe_swapped_out',
    'recipe_swapped_in',
    // Homepage reel only (src feed_reel, docs/FEED_ORDER.md "Reel signals"); the API has to know them first, an
    // unknown event or source rejects the whole batch.
    'recipe_watched',
    'recipe_watched_long',
    'recipe_opened',
    'recipe_shared',
  ]);
  const SOURCES = new Set(['funnel', 'overlay', 'reel', 'feed_reel', 'week', 'swap_sheet']);
  const SLOTS = new Set(['breakfast', 'main', 'snack', 'dessert']);
  const FLUSH_AT = 20;
  const PER_REQUEST = 50;
  const QUEUE_MAX = 200;

  const queue = [];
  const stepQueue = [];
  const stepOnce = new Set();
  // Step keys sent from this browser today (funnel_events.day is the Berlin day), so reloads, returning visits and
  // other tabs don't recount. Read and written only with analytics consent.
  const SENT_KEY = 'vhp_steps_sent';
  const berlinDay = () => {
    try {
      return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Berlin' }).format(new Date());
    } catch (e) {
      return new Date().toISOString().slice(0, 10);
    }
  };
  const sentToday = () => {
    try {
      const stored = JSON.parse(localStorage.getItem(SENT_KEY) || 'null');
      return new Set(stored?.day === berlinDay() && Array.isArray(stored.keys) ? stored.keys : []);
    } catch (e) {
      return new Set();
    }
  };
  const markSent = (keys) => {
    if (!keys.length) return;
    try {
      const sent = sentToday();
      keys.forEach((k) => sent.add(k));
      localStorage.setItem(SENT_KEY, JSON.stringify({ day: berlinDay(), keys: [...sent] }));
    } catch (e) {
      /* no storage: falls back to once per page load */
    }
  };
  const stepKey = (entry) => `${entry.s}|${entry.v ?? ''}`;

  // Visit context on every funnel step (vhp-planner docs/dashboard.md): the page type this visit started on and its
  // planner entry. Kept for this tab only (sessionStorage, no id); a visit ends after 30 minutes without a page load.
  const VISIT_KEY = 'vhp_visit';
  const VISIT_GAP = 30 * 60 * 1000;
  let visit = null;
  try {
    visit = JSON.parse(sessionStorage.getItem(VISIT_KEY) || 'null');
  } catch (e) {
    visit = null;
  }
  if (!visit || typeof visit !== 'object' || Date.now() - Number(visit.at || 0) > VISIT_GAP) visit = { l: '', en: '' };
  const saveVisit = () => {
    visit.at = Date.now();
    try {
      sessionStorage.setItem(VISIT_KEY, JSON.stringify(visit));
    } catch (e) {
      /* private mode: steps go without context */
    }
  };
  saveVisit();

  // Swipe counts for today (cards seen anywhere, feed cards seen, feed likes): milestones per browser and day, and
  // how many cards someone had seen on the planner entry / the buy tap. Stored like the sent step keys: only with
  // analytics consent, only today's three numbers. Counted in memory until then.
  const COUNT_KEY = 'vhp_swipes_today';
  const unsaved = { seen: 0, feedSeen: 0, feedLiked: 0 };
  const FEED_SOURCES = new Set(['overlay', 'reel', 'feed_reel']);
  const SEEN_MILESTONES = [1, 5, 10, 20, 40, 80];
  const LIKED_MILESTONES = [1, 3, 5, 10, 20];
  // Stored counts exist only if consent was given (saveCounts); they are read while consent is still loading too, so
  // the planner's entry_seen (counted on page load, before Shopify's consent API is there) isn't always "0".
  const dayCounts = () => {
    let stored = null;
    if (consent() !== 'no') {
      try {
        stored = JSON.parse(localStorage.getItem(COUNT_KEY) || 'null');
      } catch (e) {
        stored = null;
      }
    }
    const today = stored?.day === berlinDay() ? stored : {};
    const n = (key) => (Number(today[key]) || 0) + unsaved[key];
    return { seen: n('seen'), feedSeen: n('feedSeen'), feedLiked: n('feedLiked') };
  };
  const saveCounts = () => {
    if (!unsaved.seen && !unsaved.feedLiked) return;
    if (consent() !== 'yes') return;
    const counts = dayCounts();
    try {
      localStorage.setItem(COUNT_KEY, JSON.stringify({ day: berlinDay(), ...counts }));
      unsaved.seen = 0;
      unsaved.feedSeen = 0;
      unsaved.feedLiked = 0;
    } catch (e) {
      /* no storage: today's counts stay per page load */
    }
  };
  const countUp = (key) => {
    unsaved[key] += 1;
    const counts = dayCounts();
    if (key === 'feedSeen') SEEN_MILESTONES.forEach((at) => counts.feedSeen >= at && step('feed_seen', at));
    if (key === 'feedLiked') LIKED_MILESTONES.forEach((at) => counts.feedLiked >= at && step('feed_liked', at));
  };
  // Same buckets as the API (COUNT_BUCKETS in vhp-planner src/lib/funnelEvents.ts).
  const bucket = (n) => (n <= 0 ? '0' : n <= 2 ? '1-2' : n <= 5 ? '3-5' : n <= 10 ? '6-10' : n <= 20 ? '11-20' : n <= 40 ? '21-40' : '41+');
  // Saved likes (feed and planner deck share one document, assets/vhp-planner-likes.js; this file loads before it).
  const savedLikes = () => {
    try {
      const items = JSON.parse(localStorage.getItem(window.vhpPlannerLikes?.STORAGE_KEY || 'vhp_planner_anon') || 'null')?.likes?.items;
      return Array.isArray(items) ? items.length : 0;
    } catch (e) {
      return 0;
    }
  };
  // Start cohort (vhp-planner docs/dashboard.md, 2026-10-31): this browser's latest new start (planner_new), its Berlin
  // day and its first screen (door: monday = the week screen, goal = the Ziel slide, preset = goal picked before the
  // planner). For 7 days every step carries the door (dr) the first time it is sent after that start, so a start on
  // Monday and a buy tap on Wednesday are one chain, each step counted once per start. Only with consent, like SENT_KEY.
  const START_KEY = 'vhp_start';
  const START_DAYS = 7;
  const DOORS = new Set(['monday', 'goal', 'preset']);
  let pendingDoor = '';
  let startedHere = false;
  const readStart = () => {
    try {
      const stored = JSON.parse(localStorage.getItem(START_KEY) || 'null');
      return stored && DOORS.has(stored.door) && /^\d{4}-\d{2}-\d{2}$/.test(stored.d) && Array.isArray(stored.k) ? stored : null;
    } catch (e) {
      return null;
    }
  };
  const startOpen = (start) =>
    Boolean(start) && Date.parse(`${berlinDay()}T12:00:00Z`) - Date.parse(`${start.d}T12:00:00Z`) < START_DAYS * 86400000;
  // A changed goal pick is the same step for the chain (goal_selected counts once per value and day).
  const startKey = (entry) => (entry.s === 'goal_selected' || entry.s === 'goal_preset' ? entry.s : `${entry.s}|${entry.v ?? ''}`);

  const shownOnce = new Set();
  const shownCount = new Map(); // src -> cards seen so far this page load
  const seenDepth = new Map(); // `${src}|${id}` -> depth bucket at first sight

  const endpoint = () => `${window.vhpPlanner?.config?.apiBase || DEFAULT_API}/api/events`;

  // Product ids only (custom recipes `c<id>` and anything else are not counted).
  const productId = (id) => {
    const match = String(id ?? '').match(/(?:^|\/)(\d+)$/);
    return match ? match[1] : '';
  };

  const privacy = () => window.Shopify?.customerPrivacy || null;

  const consent = () => {
    const api = privacy();
    if (!api) return 'pending';
    try {
      if (api.analyticsProcessingAllowed()) return 'yes';
      // Banner shown but not answered yet: keep the in-memory queue until they decide.
      const current = typeof api.currentVisitorConsent === 'function' ? api.currentVisitorConsent() : null;
      return current && current.analytics === '' ? 'pending' : 'no';
    } catch (e) {
      return 'pending';
    }
  };

  const loadPrivacyApi = () => {
    if (privacy() || typeof window.Shopify?.loadFeatures !== 'function') return;
    try {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
        if (!error) flush();
      });
    } catch (e) {
      /* no consent API → nothing is sent */
    }
  };

  const send = (payload) => {
    const body = JSON.stringify(payload);
    try {
      if (navigator.sendBeacon && navigator.sendBeacon(endpoint(), body)) return;
    } catch (e) {
      /* fall through to fetch */
    }
    try {
      fetch(endpoint(), {
        method: 'POST',
        body,
        keepalive: true,
        mode: 'no-cors',
        credentials: 'omit',
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      }).catch(() => {});
    } catch (e) {
      /* counters are best effort */
    }
  };

  function flush() {
    if (!queue.length && !stepQueue.length) return;
    const state = consent();
    if (state === 'pending') return;
    saveCounts();
    const events = queue.splice(0, queue.length);
    const steps = stepQueue.splice(0, stepQueue.length);
    if (state !== 'yes') return;
    for (let i = 0; i < events.length; i += PER_REQUEST) send({ events: events.slice(i, i + PER_REQUEST) });
    // Already counted today in another page load / tab: drop it here, where consent allows reading the mark.
    const sent = sentToday();
    const fresh = steps.filter((entry) => !sent.has(stepKey(entry)));
    // Visit context, added when sent (the landing page and entry are known by then). f: this browser saw the
    // planner's first screen today, so its steps form the new-visitor funnel.
    const startedToday = sent.has('planner_new|') || fresh.some((entry) => entry.s === 'planner_new');
    const arm = testArm();
    // A new start opens a new chain; its first screen came from the planner (door) in the same boot.
    let start = readStart();
    if (fresh.some((entry) => entry.s === 'planner_new')) {
      start = { d: berlinDay(), door: DOORS.has(pendingDoor) ? pendingDoor : 'goal', k: [] };
      startedHere = true;
    } else if (start && startedHere && DOORS.has(pendingDoor)) start.door = pendingDoor;
    const chain = startOpen(start);
    fresh.forEach((entry) => {
      if (LANDING_TYPES.has(visit.l)) entry.l = visit.l;
      if (visit.en) entry.en = visit.en;
      if (startedToday) entry.f = true;
      if (arm) entry.x = arm;
      if (chain && !start.k.includes(startKey(entry))) {
        start.k.push(startKey(entry));
        entry.dr = start.door;
      }
    });
    if (start) {
      try {
        localStorage.setItem(START_KEY, JSON.stringify(start));
      } catch (e) {
        /* no storage: the chain ends with this page */
      }
    }
    markSent(fresh.map(stepKey));
    for (let i = 0; i < fresh.length; i += PER_REQUEST) send({ funnel: fresh.slice(i, i + PER_REQUEST) });
  }

  // Homepage intro test (docs/HOMEPAGE_FUNNEL.md): the arm a new device got on its first homepage load
  // (assets/vhp-collection-reels.js writes it), sent as `x` with every step for 30 days. The API whitelists the arms.
  const TEST_KEY = 'vhp_home_test';
  const ARMS = new Set(['h_intro', 'h_feed']);
  const testArm = () => {
    try {
      const raw = JSON.parse(localStorage.getItem(TEST_KEY) || 'null');
      const at = Date.parse(raw?.at || '');
      return raw && ARMS.has(raw.arm) && Date.now() - at < 30 * 86400000 ? raw.arm : '';
    } catch (e) {
      return '';
    }
  };

  // Funnel steps (unpaid setup flow): one count per step and value per browser and day (flush). Only a whitelisted
  // step name, an optional whitelisted value, a mobile/desktop flag and an in-app-browser flag are sent.
  const STEPS = new Set([
    'planner_opened',
    // Needs the planner API that accepts planner_new (deployed 2026-10-11).
    'planner_new',
    // Where a non-subscriber came in from (entryStep below). Needs the planner API that accepts planner_entry (2026-10-12).
    'planner_entry',
    // Non-subscriber page views per page type, and the page type a planner entry was tapped on (pageSeen /
    // entryStep below). Needs the planner API that accepts them (7210329, 2026-10-13).
    'page_seen',
    'entry_page',
    // Landing page, likes / cards seen at the planner entry and the buy tap, swipe milestones (vhp-planner
    // docs/dashboard.md). Needs the planner API that accepts them and the l / en / f fields (deploy it first).
    'landing',
    'entry_likes',
    'entry_seen',
    'buy_likes',
    'buy_seen',
    'feed_seen',
    'feed_liked',
    'goal_selected',
    // Goal chips before the planner (assets/vhp-plan-goal.js). Needs the planner API that accepts goal_preset (fc6828c).
    'goal_preset',
    'eckdaten_done',
    'macros_done',
    'exclusions_done',
    'deck_started',
    // "Überspringen – ich wähle für dich aus" under the deck. Needs the planner API that accepts deck_skipped (deploy it first).
    'deck_skipped',
    'plan_ready',
    'preview_viewed',
    'paywall_opened',
    'selling_plan_shown',
    'checkout_clicked',
    'planner_error',
    // Story setup (vhp-planner docs/story-setup.md). Needs the planner API from 5f35ae1 (deployed 2026-10-14).
    'story_seen',
    'pain_selected',
    'scratch_done',
    'story_closed',
    'story_sound',
    // Needs the planner API from 968f1a1 (deployed 2026-10-14).
    'story_reaction',
    // Story v2 (vhp-planner docs/story-setup-v2.md): the reason behind the goal, the hold on 3.1, the plan on the buy tap.
    // The API accepts them since f071b8a; missing here until 2026-10-16, so they were dropped before that.
    'why_selected',
    'story_hold_done',
    // A 3.1 summary row tapped to change that answer (val goal / why / body / swipe / exclusions). Needs the planner
    // API that accepts it (deploy it first: the strict schema drops the whole batch).
    'summary_edit',
    'checkout_plan',
    // The planner explainer opened from a recipe (assets/vhp-plan-goal.js), val top / end. Needs the planner API that
    // accepts it (deploy it first: the strict schema drops the whole batch).
    'planner_explainer',
    // The planner block (docs/PLANNER_BLOCK.md, assets/vhp-planner-block.js): <what>_<door>. Needs the planner API from
    // 61a1655 (deployed 2026-11-02).
    'planner_block',
    // Homepage plan (docs/HOMEPAGE_FUNNEL.md, 2026-10-18): Sören's intro card (shown / done / out_5 … out_more), a recipe
    // sheet opened (page type, or search), the giveaway popup (shown / signup / cta), a portion calculated and a "Für dich"
    // line shown (card / sheet / page), the deck's week strip opened. Needs the planner API from 590b29a.
    'intro_card',
    'recipe_open',
    'popup',
    'portion_calc',
    'portion_seen',
    // The calculator's own funnel, <seen | goal | adjust | body>_<page | card | sheet> (assets/vhp-plan-goal.js calcUse,
    // 2026-10-26). Needs the planner API that accepts it (deploy it first).
    'calc_use',
    // The two buttons on the recipe article (docs/PORTION_FIRST.md 14): shown, adapt, recipe; line = the link next to "Pro Portion".
    'recipe_choice',
    'week_strip',
    // "Schick mir meine Woche" (docs/HOMEPAGE_FUNNEL.md 5, assets/vhp-planner-weekmail.js): the e-mail sheet shown (row /
    // exit), the week saved from there, the restore link opened (ok / gone). Needs the planner API that accepts them.
    'week_ask',
    'week_saved',
    'week_restored',
    // Recipe page (docs/RECIPE_FUNNEL.md 0.4, assets/vhp-main-recipe.js + vhp-cook.js): open / video / ings / end /
    // serv / cook / cook_done / share / print. Needs the planner API that accepts it (deploy it first).
    'recipe_use',
    // Planner page plan (docs/PLANNER_PAGE_FUNNEL.md, assets/vhp-planner-funnel.js): what was done on the free preview,
    // the first screen's boot time, the "Fast geschafft" sheet on × (stay / leave), paywall first after the hold (shown /
    // preview). Needs the planner API that accepts them (deploy it first: the strict schema drops the whole batch).
    'preview_use',
    'boot',
    // How long the first slide stood before it was answered or left (docs/CR_AUDIT.md 2; answered_* / left_* with
    // lt3 / 3_10 / 10p). Needs the planner API that accepts it (deploy it first).
    'goal_dwell',
    // The goal tile tapped on slide 1, at the tap (goal_selected comes after Sören's reply). Needs the planner API that
    // accepts it (deploy it first).
    'goal_tap',
    // Slide 1 drop diagnostics (assets/vhp-planner-funnel.js, 2026-10-22): visibility at the first screen's boot, the
    // first touch on slide 1 (tile / other / none). Needs the planner API that accepts them (deploy it first).
    'boot_vis',
    'goal_touch',
    'story_exit',
    'pw_first',
    // Her Monday, the planner landers' first screen (docs/FIRST_SCREEN.md 9): seen / adjust / rebuilt / go. Needs the
    // planner API that accepts it (289f3c5, deployed 2026-10-29).
    'monday',
    // "Weiter in Safari / Chrome" for in-app visitors (docs/PLANNER_PAGE_FUNNEL.md 4.3): shown / tap / copy / ok.
    'pay_handoff',
    // Paid users: page loads that opened the planner / used a feature (no values).
    'paid_opened',
    'use_shopping_list',
    'use_swap',
    'use_lock',
    // Needs the planner API that accepts use_copy (deploy it first).
    'use_copy',
    'use_remix_day',
    'use_remix_week',
    'use_cook_mode',
  ]);
  const device = () => (window.matchMedia?.('(max-width: 959px)').matches ? 'm' : 'd');
  const inAppBrowser = () => /Instagram|FBAN|FBAV|FB_IAB|TikTok|musical_ly/i.test(navigator.userAgent || '');
  const step = (s, v) => {
    if (!STEPS.has(s)) return;
    const value = v === undefined || v === null || v === '' ? undefined : String(v).slice(0, 12);
    const key = `${s}|${value ?? ''}`;
    if (stepOnce.has(key)) return;
    stepOnce.add(key);
    const entry = { s, d: device(), iab: inAppBrowser() };
    if (value !== undefined) entry.v = value;
    stepQueue.push(entry);
    if (stepQueue.length > QUEUE_MAX) stepQueue.splice(0, stepQueue.length - QUEUE_MAX);
    try {
      window.Shopify?.analytics?.publish?.(`vhp_${s}`, entry);
    } catch (err) {
      /* optional */
    }
    // The checkout click leaves the page, so send right away (still gated by consent), with how many likes and cards
    // it took (likes include the planner deck's).
    if (s === 'checkout_clicked') {
      step('buy_likes', bucket(savedLikes()));
      step('buy_seen', bucket(dayCounts().seen));
      flush();
    }
  };

  const track = (e, { id, slot, src } = {}) => {
    const pid = productId(id);
    if (!EVENTS.has(e) || !pid || !SLOTS.has(slot) || !SOURCES.has(src)) return;
    const event = { e, id: pid, slot, src };
    // Swipe depth when this recipe was first seen (for the position-bias correction in the rollup).
    const depth = seenDepth.get(`${src}|${pid}`);
    if (depth !== undefined) event.pos = depth;
    queue.push(event);
    if (queue.length > QUEUE_MAX) queue.splice(0, queue.length - QUEUE_MAX);
    if (e === 'recipe_liked' && FEED_SOURCES.has(src)) countUp('feedLiked');
    try {
      // Mirror for Shopify customer-events pixels (GA/Klaviyo later). Shopify gates pixels by consent.
      window.Shopify?.analytics?.publish?.(`vhp_${e}`, event);
    } catch (err) {
      /* optional */
    }
    if (queue.length >= FLUSH_AT) flush();
  };

  // Once per recipe and source per page load (re-renders and back/forth don't recount).
  const shown = ({ id, slot, src }) => {
    const key = `${src}|${productId(id)}`;
    if (shownOnce.has(key)) return;
    shownOnce.add(key);
    // Depth bucket: 0 = 1st-5th card seen from this source, 1 = 6th-10th, 2 = 11th-20th, 3 = later.
    const seen = shownCount.get(src) || 0;
    shownCount.set(src, seen + 1);
    seenDepth.set(key, seen < 5 ? 0 : seen < 10 ? 1 : seen < 20 ? 2 : 3);
    track('recipe_shown', { id, slot, src });
    countUp('seen');
    if (FEED_SOURCES.has(src)) countUp('feedSeen');
  };

  // An event once per recipe and source per page load (the reel's watch, open and share signals): scrolling back to a
  // card or opening its recipe twice counts once, like an impression.
  const eventOnce = new Set();
  const once = (e, { id, slot, src } = {}) => {
    const key = `${e}|${src}|${productId(id)}`;
    if (eventOnce.has(key)) return;
    eventOnce.add(key);
    track(e, { id, slot, src });
  };

  // Undo within the page: drop the newest matching event if it has not been sent yet.
  const retract = (e, id) => {
    const pid = productId(id);
    for (let i = queue.length - 1; i >= 0; i -= 1) {
      if (queue[i].e === e && queue[i].id === pid) {
        queue.splice(i, 1);
        return true;
      }
    }
    return false;
  };

  // planner_entry: once per visit, at its first navigation into the planner (not on reloads / back), non-subscribers only. The button
  // comes from assets/vhp-track.js (sessionStorage, last 30 min); outside links can carry ?src=; else the referrer.
  const ENTRY_KEY = 'vhp_planner_entry';
  const ENTRY_MAP = {
    tabbar: 'tabbar',
    topbar: 'topbar',
    resume_tab: 'resume_tab',
    resume_top: 'resume_top',
    sidepanel: 'sidepanel',
    profile: 'profile',
    explore_goals: 'explore',
    likes_page: 'likes',
    plan_page: 'plan_page',
    feed_card: 'feed_card',
    feed_ready: 'feed_ready',
    feed_done: 'feed_done',
    paywall_card: 'card_plan',
    paywall_sheet: 'sheet_plan',
    paywall_cook: 'cook_plan',
    paywall_page: 'recipe_plan',
    plan_added_toast: 'toast',
    exclusive: 'exclusive',
    // Page modules (docs/entry.md in vhp-planner).
    recipe_goal: 'recipe_goal',
    // The card under a recipe's method, through the planner explainer (2026-10-16).
    recipe_end: 'recipe_end',
    grid_tile: 'grid_tile',
    explore_top: 'explore_top',
    likes_bar: 'likes_bar',
    deck_card: 'deck_card',
    // Homepage plan (docs/HOMEPAGE_FUNNEL.md): the deck's week strip, the "Für dich" line, the card's portion calculator.
    feed_strip: 'feed_strip',
    portion_line: 'portion_line',
    card_calc: 'card_calc',
    // Homepage collage intro (docs/HOME_INTRO.md): its "Meine Woche planen" button.
    hero: 'hero',
    // Recipe page plan (docs/RECIPE_FUNNEL.md 3): cook mode's "Fertig" card, the shopping-list line under the ingredients.
    cook_done: 'cook_done',
    recipe_list: 'recipe_list',
    // The planner line under the dish on a recipe page (docs/RECIPE_PAGE_REBUILD.md 2, 2026-11-02).
    recipe_top: 'recipe_top',
    // The planner card in the app menu (docs/NAV_MENU.md 3, docs/PLANNER_BLOCK.md 5). Needs the planner API that accepts it
    // (deployed 2026-11-02); counted as internal before.
    menu: 'menu',
  };
  const PAGE_TYPES = new Set(['home', 'collection', 'recipe', 'explore', 'likes', 'profile', 'plan', 'other']);
  // dm / reel: Instagram DM links and reel captions (docs/PLANNER_PAGE_FUNNEL.md 0.5; the API accepts them).
  const ENTRY_PARAMS = new Set(['ig', 'story', 'bio', 'email', 'exclusive', 'dm', 'reel']);
  let entryPage = null;
  const entryValue = () => {
    // The restore link in mail 1 (?week=<token>, assets/vhp-planner-weekmail.js); the funnel drops it from the URL later.
    if (/^[A-Za-z0-9_-]{24}$/.test(new URLSearchParams(location.search).get('week') || '')) {
      try {
        sessionStorage.removeItem(ENTRY_KEY);
      } catch (err) {
        /* nothing stored */
      }
      // ?pay=1: the same week handed over from Instagram's browser to pay here (4.3).
      return new URLSearchParams(location.search).get('pay') === '1' ? 'handoff' : 'week_mail';
    }
    let stored = null;
    try {
      stored = JSON.parse(sessionStorage.getItem(ENTRY_KEY) || 'null');
      sessionStorage.removeItem(ENTRY_KEY);
    } catch (err) {
      stored = null;
    }
    if (stored && Date.now() - Number(stored.at || 0) < 30 * 60 * 1000) {
      // The page type the button was tapped on (assets/vhp-track.js), for entry_page.
      if (PAGE_TYPES.has(stored.p)) entryPage = stored.p;
      return ENTRY_MAP[stored.s] || 'internal';
    }
    const param = new URLSearchParams(location.search).get('src');
    if (param && ENTRY_PARAMS.has(param)) return param;
    let ref = null;
    try {
      ref = document.referrer ? new URL(document.referrer) : null;
    } catch (err) {
      ref = null;
    }
    if (ref && ref.origin === location.origin) return 'internal';
    if (ref) return /instagram|facebook|tiktok/i.test(ref.hostname) ? 'ig' : 'link';
    return inAppBrowser() ? 'inapp' : 'direct';
  };
  const isSubscriber = () => {
    try {
      return JSON.parse(document.getElementById('vhp-account-config')?.textContent || '{}').subscriber === true;
    } catch (err) {
      return false;
    }
  };
  const entryStep = () => {
    if (!document.body?.classList.contains('vhp-planner-page')) return;
    const nav = performance.getEntriesByType?.('navigation')?.[0];
    if (nav && nav.type !== 'navigate') return;
    if (isSubscriber()) return;
    const entry = entryValue();
    // Once per visit, the first way in wins: a way back (tabbar, resume) counted again pushed a landing's planner
    // share over 100 % and took the visit's later steps away from the door it came through (2026-10-26).
    if (visit.en) return;
    step('planner_entry', entry);
    if (entryPage) step('entry_page', entryPage);
    // What they brought into the planner: saved likes (all time) and recipe cards seen today.
    step('entry_likes', bucket(savedLikes()));
    step('entry_seen', bucket(dayCounts().seen));
    visit.en = entry;
    saveVisit();
  };

  // landing: the first page of a visit (non-subscribers), "planner" when the visit starts in the planner.
  const LANDING_TYPES = new Set([...PAGE_TYPES, 'planner']);
  const landingStep = (type) => {
    if (visit.l) return;
    const onPlanner = document.body?.classList.contains('vhp-planner-page');
    visit.l = onPlanner ? 'planner' : LANDING_TYPES.has(type) ? type : 'other';
    saveVisit();
    if (!isSubscriber()) step('landing', visit.l);
  };

  // page_seen: one per page type per document for non-subscribers (the in-page tab router keeps one document, so a
  // tab visited twice counts once). The planner page itself is not a page type.
  const pageSeen = (type) => {
    if (!PAGE_TYPES.has(type) || isSubscriber()) return;
    step('page_seen', type);
  };

  // Counters must never break the caller: every public method swallows its own errors.
  const safe = (fn) => (...args) => {
    try {
      return fn(...args);
    } catch (err) {
      return undefined;
    }
  };
  const safeFlush = safe(flush);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') safeFlush();
  });
  window.addEventListener('pagehide', safeFlush);
  document.addEventListener('visitorConsentCollected', safeFlush);
  safe(loadPrivacyApi)();
  // The Plan tab is prerendered from the moment a finger is on it (speculation rules in layout/theme.liquid):
  // wait until it is really shown, after the click stored its source.
  if (document.prerendering) document.addEventListener('prerenderingchange', safe(entryStep), { once: true });
  else safe(entryStep)();
  // After the deferred scripts (assets/vhp-track.js knows the page type; vhp-main-collection loads this file
  // without defer, before it), and only once the page is really shown.
  const firstPageSeen = safe(() => {
    const type = window.vhpTrack?.pageType?.();
    landingStep(type);
    pageSeen(type);
  });
  const whenShown = () => (document.prerendering ? document.addEventListener('prerenderingchange', firstPageSeen, { once: true }) : firstPageSeen());
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', whenShown, { once: true });
  else whenShown();
  document.addEventListener('vhp:page-type', safe((event) => pageSeen(event.detail?.type)));

  // The visit's context for the order (docs/PLANNER_PAGE_FUNNEL.md 0.3: the buy tap writes it as cart attributes), only
  // with analytics consent, like the steps: landing page type, planner entry, Instagram-type in-app browser, and whether
  // this browser started the setup today. Nothing about the person.
  const context = () => {
    if (consent() !== 'yes') return null;
    const startingNow = stepQueue.some((entry) => entry.s === 'planner_new');
    const started = sentToday().has('planner_new|') || startingNow;
    const start = readStart();
    return {
      land: LANDING_TYPES.has(visit.l) ? visit.l : '',
      ent: visit.en || '',
      iab: inAppBrowser(),
      fresh: started,
      // The door of the start this buy belongs to (7 days, like the steps' dr).
      door: startingNow ? (DOORS.has(pendingDoor) ? pendingDoor : 'goal') : startOpen(start) ? start.door : '',
    };
  };

  // The planner says which first screen a new start got (monday / goal / preset), right with planner_new / goal_preset.
  const door = (value) => {
    if (DOORS.has(value)) pendingDoor = value;
  };

  window.vhpPlannerEvents = {
    track: safe(track),
    shown: safe(shown),
    once: safe(once),
    retract: safe(retract),
    step: safe(step),
    flush: safeFlush,
    context: safe(context),
    door: safe(door),
  };
})();
